import {Injectable} from '@angular/core';
import {BehaviorSubject, concat, from, Observable, Subject, toArray} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpEventType, HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {BaseService} from './_base.service';
import {ShadeService} from './shade.service';
import {ConvoId, TopicId, UUID} from '../models/uuid';
import {UserId} from '../../shared/models/user';
import {MotiDataSource, Summary, Upload} from '../models/moti-data-source';


export interface ByteProgress {
  sent: number,
  total: number
}


interface TopicConfig {
  userId?: never;
  teamId?: never;
  convoId?: never;
  topicId: TopicId;

}


interface ConvoConfig {
  userId?: never;
  teamId?: never;
  convoId: ConvoId;
  topicId?: never;
}


interface TeamConfig {
  userId?: never;
  teamId: UUID;
  convoId?: never;
  topicId?: never;
}


interface UserConfig {
  userId: UserId;
  teamId?: never;
  convoId?: never;
  topicId?: never;
}


export type UploadConfig =
  | TopicConfig
  | ConvoConfig
  | TeamConfig
  | UserConfig;


export interface UploadTracker {
  upload$: Observable<Upload>;
  bytes$: Observable<ByteProgress>;
}


export interface UploadArrayTracker {
  uploadArray$: Observable<Upload[]>;
  bytes$: Observable<ByteProgress>;
}


const MAX_UPLOAD_SIZE_MB = 100;
const MAX_SIMULTANEOUS_UPLOADS = 8;
const ERROR_TOO_LARGE = Error('file too large to upload');
const ERROR_UNSUPPORTED_MIME_TYPE = Error('file has unsupported mime type');


export interface DataSourceQuery {
  dataSourceIds?: UUID[];
  convoId?: ConvoId;
  topicId?: TopicId;
  userId?: UserId;
  deleted?: boolean;
}


@Injectable({
  providedIn: 'root',
})
export class DataSourceService extends BaseService {
  dataSourceMap: { [id: string]: MotiDataSource } = {};


  constructor(
    private http: HttpClient,
    private shade: ShadeService,
  ) {
    super();
  }


  getDataSource$(dsId: UUID): Observable<MotiDataSource> {
    return new Observable<MotiDataSource>(subscriber => {
      if (this.dataSourceMap[dsId]) {
        subscriber.next(this.dataSourceMap[dsId]);
        subscriber.complete();
        return;
      }

      this.http.get<MotiDataSource>(`${this.httpHost}/data-sources/${dsId}`).subscribe({
        next: ds => {
          this.dataSourceMap[ds.id] = ds;
          subscriber.next(this.dataSourceMap[dsId]);
          subscriber.complete();
        },
        error: err => {
          subscriber.error(err);
        },
      });
    });
  }


  queryDataSources$(query: DataSourceQuery): Observable<MotiDataSource[]> {
    if (query.dataSourceIds) {
      // @ts-ignore
      query.dataSourceIds = query.dataSourceIds.toString();
    }

    return this.http.get<MotiDataSource[]>(`${this.httpHost}/data-sources`, {
      params: query as HttpParams,
    }).pipe(
      map(dss => {
        for (let ds of dss) {
          this.dataSourceMap[ds.id] = ds;
        }

        return dss;
      }),
    );
  }


  patchDataSource$(dataSourceId: UUID, patch: {
    name?: string,
  }): Observable<any> {
    return this.http.patch(`${this.httpHost}/data-sources/${dataSourceId}`, patch);
  }


  deleteDataSource$(dataSourceId: UUID) {
    return this.http.delete(`${this.httpHost}/data-sources/${dataSourceId}`);
  }


  /**
   *
   * @param file
   * @throws Error if upload would be rejected by server
   */
  validateUpload(file: File) {
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      this.shade.errorDialog(`The file you are trying to upload exceeds the maximum supported file size: ${MAX_UPLOAD_SIZE_MB} MB`);
      throw ERROR_TOO_LARGE;
    }

    // if (!SUPPORTED_UPLOAD_MIME_TYPES.includes(file.type)) {
    //   throw ERROR_UNSUPPORTED_MIME_TYPE;
    // }
  }


  getUploads$(query: DataSourceQuery): Observable<Upload[]> {
    return new Observable<Upload[]>(subscriber => {
      this.queryDataSources$(query).pipe(
        map((dss): Upload[] => {
          const uploads = dss.filter(ds => ds.type === 'upload');
          return uploads as Upload[];
        }),
      ).subscribe({
        next: uploads => {
          subscriber.next(uploads);
          subscriber.complete();
        },
        error: err => {
          subscriber.error(err);
        },
      });
    });
  }


  /**
   * This is private because there is no use case anywhere for supporting only a single upload.
   *
   * Hence there are no validations here. If this ever becomes public, validations need to be added.
   *
   * @param file
   * @param config
   * @private
   */
  private createUpload$(file: File, config: UploadConfig): UploadTracker {
    const bytes$ = new BehaviorSubject({sent: 0, total: file.size});
    const upload$ = new Observable<Upload>(subscriber => {
      const formData = new FormData();
      formData.append('file', file, file.name);

      let params = new HttpParams();

      for (let key in config) {
        let value = config[key as keyof UploadConfig];
        params = params.set(key, value);
      }

      this.http.post<Upload>(`${this.httpHost}/uploads`, formData, {
        reportProgress: true,
        observe: 'events',
        params: params,
      }).subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress) {
            bytes$.next({sent: event.loaded, total: file.size});
          } else if (event.type === HttpEventType.Response) {
            this.dataSourceMap[event.body.id] = event.body;

            subscriber.next(event.body);
            subscriber.complete();
            bytes$.complete();
          }
        },
        error: err => {
          this.shade.errorDialog(err['error']?.message);
          subscriber.error(err['error']);
          bytes$.error(err);
        },
      });
    });

    return {
      bytes$,
      upload$,
    };
  }


  /**
   * For uploading one or more files and receiving a stream of upload objects.
   *
   * This will end up calling the API once per file.
   *
   * @param files
   * @param config
   * @see createAndCombineUploads$ - For uploading multiple files at a time and getting back an array of them all
   *    instead of a stream of each individual result
   */
  createAndStreamUploads$(files: FileList, config: UploadConfig): UploadTracker {
    const bytes$ = new Subject<ByteProgress>();
    const upload$ = new Observable<Upload>(subscriber => {
      if (files.length > MAX_SIMULTANEOUS_UPLOADS) {
        subscriber.error(`Only up to ${MAX_SIMULTANEOUS_UPLOADS} files can be uploaded at a time.`);
        return;
      }

      const uploadRequests$: Observable<Upload>[] = [];

      let combinedSent: { [i: number]: number } = {};
      let combinedTotal: number = 0;

      for (let _i = 0; _i < files.length; _i++) {
        try {
          const i = _i;
          const file = files[i];
          this.validateUpload(file);

          combinedTotal += file.size;
          combinedSent[i] = 0;

          const tracker = this.createUpload$(file, config);

          uploadRequests$.push(tracker.upload$);

          tracker.bytes$.subscribe({
            next: bytes => {
              combinedSent[i] = bytes.sent;

              let sent: number = 0;

              for (let _sent of Object.values(combinedSent)) {
                sent += _sent;
              }

              bytes$.next({
                sent: sent,
                total: combinedTotal,
              });
            },
          });
        } catch (e) {
          let err = this.formatUploadError(e);
          subscriber.error(err);
          bytes$.error(err);
          return;
        }
      }

      bytes$.next({sent: 0, total: combinedTotal});

      concat(...uploadRequests$).subscribe({
        next: upload => {
          subscriber.next(upload);
        },
        error: (err: HttpErrorResponse) => {
          subscriber.error(err);
          bytes$.error(err);
        },
        complete: () => {
          subscriber.complete();
          bytes$.complete();
        },
      });
    });

    return {
      bytes$,
      upload$,
    };
  }


  /**
   * For uploading multiple files at once.
   *
   * This will end up calling the API once per file.
   *
   * This calls createAndStreamUploads$ and combines every upload's result into an array which is itself
   * emitted once before calling complete.
   *
   * The only difference between this and createUploads$ is the latter returns an observable of a stream
   * of upload objects, while this returns an observable that will emit only one array of upload objects.
   *
   * Furthermore, this function validates all uploads prior to attempting to upload any one.
   *
   * Usage exhaustion check takes place in the call to createAndStreamUploads$
   *
   * @param files
   * @param config
   */
  createAndCombineUploads$(files: FileList, config: UploadConfig): UploadArrayTracker {
    const bytes$ = new Subject<ByteProgress>();
    const uploadArray$ = new Observable<Upload[]>(subscriber => {
      for (let i = 0; i < files.length; i++) {
        try {
          const file = files[i];
          this.validateUpload(file);
        } catch (e) {
          const err = this.formatUploadError(e);
          console.error(err);
          subscriber.error(err);
          return;
        }
      }

      const uploadTracker = this.createAndStreamUploads$(files, config);

      uploadTracker.bytes$.subscribe({
        next: progress => {
          bytes$.next(progress);
        },
        error: err => {
          bytes$.error(err);
        },
        complete: () => {
          bytes$.complete();
        },
      });

      from(uploadTracker.upload$).pipe(
        toArray(),
      ).subscribe({
        next: uploads => {
          subscriber.next(uploads);
          subscriber.complete();
        },
        error: (err) => {
          subscriber.error(err);
        },
      });
    });

    return {
      bytes$,
      uploadArray$,
    };
  }


  private formatUploadError(e: any): string {
    let err: string;

    if (e === ERROR_TOO_LARGE) {
      err = `File exceeds the maximum supported size for uploads (${MAX_UPLOAD_SIZE_MB} MB)`;
      // } else if (e === ERROR_UNSUPPORTED_MIME_TYPE) {
      //   err = `File is not one of the supported types. Supported types include files whose standard extensions include: ${SUPPORTED_UPLOAD_EXTENSIONS.map(e => '.' + e.toUpperCase()).join(', ')}`;
    } else {
      console.error(e);
    }

    return err;
  }


  getSignedUrl$(dsId: UUID): Observable<string> {
    return new Observable<string>(subscriber => {
      this.http.post<string>(`${this.httpHost}/uploads/${dsId}/signed-urls`, null).subscribe({
        next: url => {
          subscriber.next(url);
          subscriber.complete();
        },
        error: () => {
          subscriber.error();
        },
      });
    });
  }


  getUploadSummary$(dsId: UUID): Observable<Summary> {
    return this.http.get<Summary>(`${this.httpHost}/uploads/${dsId}/summaries`);
  }
}
